import { useEffect } from 'react';

import { MarketingHeader, MarketingFooter } from '../components/MarketingChrome';
import { ResourceSidebar } from '../components/ResourceSidebar';
import '../components/landing.css';
import './getting-started.css';

const APP_STORE_URL = 'https://apps.apple.com/sa/app/paddiq/id6785660120';
const PAGE_TITLE = 'Troubleshoot session analysis on iPhone | Paddiq';

export function IPhoneSessionTroubleshootingPage() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;

    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="landing marketing-home mh-interior">
      <a className="mh-skip" href="#main">
        Skip to content
      </a>
      <MarketingHeader />

      <main id="main">
        <section className="guide-hero">
          <div className="landing-container guide-hero__inner">
            <p className="section-kicker">iPhone troubleshooting guide</p>
            <h1>Fix missing laps, maps, and traces on iPhone.</h1>
            <p className="guide-hero__lead">
              Follow the message on screen to recover a missing session, choose a usable lap source,
              and restore the analysis controls that depend on GPS, laps, or circuit details.
            </p>
            <div className="guide-hero__actions">
              <a
                className="landing-btn landing-btn--primary landing-btn--lg"
                href="/resources/analyze-sessions-iphone"
              >
                Review the analysis workflow
              </a>
              <a className="landing-btn landing-btn--ghost landing-btn--lg" href="/support">
                Contact support
              </a>
            </div>
            <p className="guide-hero__meta">iPhone guide · Reviewed October 15, 2026</p>
          </div>
        </section>

        <section className="guide-section">
          <div className="landing-container guide-layout">
            <aside className="guide-summary" aria-label="Guide summary">
              <ResourceSidebar currentPath="/resources/troubleshoot-sessions-iphone" />
              <p className="guide-summary__label">In this guide</p>
              <ol>
                <li>
                  <a href="#session">Find a missing session</a>
                </li>
                <li>
                  <a href="#gps">Recover the map</a>
                </li>
                <li>
                  <a href="#laps">Restore lap choices</a>
                </li>
                <li>
                  <a href="#traces">Restore traces and comparisons</a>
                </li>
                <li>
                  <a href="#turns">Restore overlays and turns</a>
                </li>
              </ol>
              <div className="guide-summary__note">
                <strong>Work in this order</strong>
                <span>
                  Confirm the session first, then GPS, lap choices, traces, and finally the
                  circuit-dependent analysis tools.
                </span>
              </div>
            </aside>

            <article className="guide-content">
              <section className="guide-step" id="session">
                <div className="guide-step__number" aria-hidden="true">
                  1
                </div>
                <div>
                  <p className="guide-step__eyebrow">Missing session</p>
                  <h2>Start in Sessions and confirm the upload.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Open <strong>Sessions</strong> and tap the session name above the map.
                    </li>
                    <li>
                      Open the selector under <strong>1 · Session</strong> and look for the run.
                    </li>
                    <li>
                      If it is missing, confirm that the assignment and upload flow finished on the
                      iPhone used for the import.
                    </li>
                    <li>Confirm that you are signed in to the intended account and team.</li>
                  </ol>
                  <div className="guide-callout">
                    For a download that has not uploaded, open <strong>Loggers</strong> →{' '}
                    <strong>Saved</strong> on the iPhone used for the import. Look under{' '}
                    <strong>Ready to attach</strong> for a run marked{' '}
                    <strong>Awaiting assignment</strong> or <strong>Tap to prepare</strong>. Tap it
                    or <strong>Attach</strong>, then finish <strong>Verify circuit</strong> and the
                    assignment and upload flow instead of importing the same file again. While
                    uploads are paused, the section is labeled <strong>Saved on this device</strong>
                    .
                  </div>
                  <p>
                    This Saved list is titled Sessions. The <strong>Sessions</strong> tab in the
                    bottom navigation opens uploaded-session analysis; it does not show pending
                    local downloads from another iPhone.
                  </p>
                </div>
              </section>

              <section className="guide-step" id="gps">
                <div className="guide-step__number" aria-hidden="true">
                  2
                </div>
                <div>
                  <p className="guide-step__eyebrow">No GPS for this session</p>
                  <h2>Try a complete lap, then another session.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Open <strong>Session &amp; laps</strong> and choose another selectable flying
                      lap.
                    </li>
                    <li>If every lap has the same issue, choose another session.</li>
                    <li>
                      If GPS should have been recorded, keep the original logger file available and
                      contact support before importing it again.
                    </li>
                  </ol>
                  <p>
                    A saved circuit can provide an outline, but circuit lap timing still needs
                    usable recorded GPS crossings. Choosing a circuit cannot replace GPS that is
                    missing from the session.
                  </p>
                </div>
              </section>

              <section className="guide-step" id="laps">
                <div className="guide-step__number" aria-hidden="true">
                  3
                </div>
                <div>
                  <p className="guide-step__eyebrow">Lap source</p>
                  <h2>Choose the recovery path that matches the recording.</h2>
                  <ol className="guide-instructions">
                    <li>
                      If you see <strong>Circuit laps need a start/finish line</strong>, choose a
                      saved circuit or tap <strong>Set up circuit</strong>.
                    </li>
                    <li>
                      In <strong>Circuit setup</strong>, check the circuit and direction, build the
                      laps, then save them.
                    </li>
                    <li>
                      Tap <strong>Use raw laps</strong> when you want the logger&apos;s recorded lap
                      beacons instead.
                    </li>
                    <li>
                      If you see <strong>No raw laps in this session</strong>, return to{' '}
                      <strong>Circuit Laps</strong> and build laps from a saved start/finish line.
                    </li>
                  </ol>
                </div>
              </section>

              <figure className="guide-workflow guide-workflow--wide-card">
                <img
                  src="/iphone-circuit-laps-recovery.webp"
                  width="1104"
                  height="476"
                  alt="Paddiq iPhone recovery card explaining that circuit laps need a start and finish line, with Set up circuit and Use raw laps actions"
                />
                <figcaption>
                  Choose Set up circuit when the recording has GPS and needs a saved start/finish
                  line. Choose Use raw laps when the logger already recorded usable lap beacons.
                </figcaption>
              </figure>

              <section className="guide-step" id="traces">
                <div className="guide-step__number" aria-hidden="true">
                  4
                </div>
                <div>
                  <p className="guide-step__eyebrow">Traces and comparisons</p>
                  <h2>Select the primary lap before opening Lap traces.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Open <strong>Session &amp; laps</strong> and choose a selectable primary lap.
                    </li>
                    <li>Close the panel and wait for the session and lap to finish loading.</li>
                    <li>
                      Open <strong>Lap traces</strong>. If a channel is missing from{' '}
                      <strong>Add traces</strong>, try a lap that recorded that channel.
                    </li>
                    <li>
                      For a missing comparison, confirm both <strong>Compare session 1</strong> and{' '}
                      <strong>Compare lap</strong>, then keep the app online while the second run
                      loads.
                    </li>
                  </ol>
                  <p>
                    Keep the primary and comparison laps in the same Circuit Laps or Raw laps mode
                    and use the same circuit configuration whenever possible.
                  </p>
                </div>
              </section>

              <section className="guide-step" id="turns">
                <div className="guide-step__number" aria-hidden="true">
                  5
                </div>
                <div>
                  <p className="guide-step__eyebrow">Overlays and turns</p>
                  <h2>Check the data and circuit each tool needs.</h2>
                  <ol className="guide-instructions">
                    <li>
                      If <strong>Overlays</strong> is unavailable, choose a lap or session with a
                      complete GPS-speed recording.
                    </li>
                    <li>
                      If an overlay is on but shows no colored sections, try another complete lap.
                      The recording may not contain enough sustained deceleration.
                    </li>
                    <li>
                      If <strong>Turns</strong> shows <strong>No authored turns</strong>, assign the
                      intended circuit with saved turn boundaries.
                    </li>
                    <li>
                      If no turns intersect the lap, recheck the circuit configuration, direction,
                      lap source, and selected lap.
                    </li>
                  </ol>
                  <div className="guide-callout">
                    An empty braking or coasting overlay does not prove that the driver never braked
                    or coasted. Review the speed trace and other recorded channels before drawing a
                    conclusion.
                  </div>
                </div>
              </section>

              <section className="guide-troubleshooting" id="support-details">
                <p className="guide-step__eyebrow">Support handoff</p>
                <h2>Include the details that identify the issue</h2>
                <div className="guide-troubleshooting__grid">
                  <article>
                    <h3>Session</h3>
                    <p>Include the session name, recorded date, logger type, and selected lap.</p>
                  </article>
                  <article>
                    <h3>What you selected</h3>
                    <p>
                      Include Circuit Laps or Raw laps, the circuit, and the exact message shown.
                    </p>
                  </article>
                  <article>
                    <h3>Phone</h3>
                    <p>Include the iPhone model, iOS version, and when the problem occurred.</p>
                  </article>
                  <article>
                    <h3>Keep sign-in links private</h3>
                    <p>
                      Never send your password, setup link, password-reset link, or another sign-in
                      code.
                    </p>
                  </article>
                </div>
              </section>

              <section className="guide-cta">
                <div>
                  <p className="guide-step__eyebrow">Still stuck?</p>
                  <h2>Send support the exact message.</h2>
                  <p>
                    Keep the original logger file available. Support will tell you if and how to
                    provide it after reviewing the session details.
                  </p>
                </div>
                <div className="guide-cta__actions">
                  <a className="landing-btn landing-btn--primary landing-btn--lg" href="/support">
                    Contact support
                  </a>
                  <a
                    className="landing-btn landing-btn--ghost landing-btn--lg"
                    href="/resources/import-mychron-iphone"
                  >
                    Review iPhone import
                  </a>
                </div>
              </section>
            </article>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
}
